
import React, { useState, useEffect } from 'react';
import OnboardingLayout from '../../OnboardingLayout';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { DollarSign, Check, AlertCircle } from 'lucide-react';
import { createJournalistOnboarding } from '@/services/stripeService';

interface MonetizationSetupProps {
  formData: any;
  updateFormData: (data: any) => void;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
}

const MonetizationSetup: React.FC<MonetizationSetupProps> = ({
  formData,
  updateFormData,
  currentStep,
  totalSteps,
  onNext,
  onPrevious
}) => {
  const [loading, setLoading] = useState(false);
  const [monetizationModel, setMonetizationModel] = useState(formData.monetizationModel || 'subscription');
  const [articlePrice, setArticlePrice] = useState(formData.articlePrice || 0.99);
  const [subscriptionPrice, setSubscriptionPrice] = useState(formData.subscriptionPrice || 4.99);
  const [acceptedTerms, setAcceptedTerms] = useState(formData.acceptedTerms || false);
  
  const handleSubmit = async () => {
    const updatedData = {
      monetizationModel,
      articlePrice,
      subscriptionPrice,
      acceptedTerms
    };
    
    updateFormData(updatedData);
    
    if (acceptedTerms) {
      setLoading(true);
      try {
        // Initiate Stripe Connect onboarding
        await createJournalistOnboarding();
      } catch (error) {
        console.error('Error in onboarding:', error);
        // We'll still proceed to the next step even if Stripe onboarding fails
        // as the user can complete it later
        onNext();
      }
      setLoading(false);
    } else {
      onNext();
    }
  };

  return (
    <OnboardingLayout
      title="Monetization Setup"
      subtitle="Set up how you'll earn from your content"
      currentStep={currentStep}
      totalSteps={totalSteps}
      onNext={handleSubmit}
      onPrevious={onPrevious}
      nextLabel={loading ? "Processing..." : "Continue"}
      nextDisabled={loading}
    >
      <div className="max-w-3xl mx-auto space-y-8">
        <Card>
          <CardHeader>
            <CardTitle>Choose Your Monetization Model</CardTitle>
            <CardDescription>
              Select how you want to monetize your content on DirectPress
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup
              value={monetizationModel}
              onValueChange={setMonetizationModel}
              className="space-y-4"
            >
              <div className={`border rounded-lg p-4 ${
                monetizationModel === 'subscription' ? 'border-primary bg-primary/5' : ''
              }`}>
                <div className="flex items-start gap-3">
                  <RadioGroupItem value="subscription" id="subscription" className="mt-1" />
                  <div>
                    <Label htmlFor="subscription" className="font-medium">Subscription Model</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Readers pay a monthly fee to access all your content
                    </p>
                    
                    {monetizationModel === 'subscription' && (
                      <div className="mt-4">
                        <Label htmlFor="subscription-price">Monthly Subscription Price ($)</Label>
                        <div className="flex items-center gap-3 mt-2">
                          <DollarSign className="h-4 w-4 text-muted-foreground" />
                          <Input
                            id="subscription-price"
                            type="number"
                            min="0.99"
                            max="99.99"
                            step="1"
                            value={subscriptionPrice}
                            onChange={(e) => setSubscriptionPrice(parseFloat(e.target.value))}
                            className="w-24"
                          />
                          <Slider
                            value={[subscriptionPrice]}
                            min={0.99}
                            max={20}
                            step={1}
                            onValueChange={(value) => setSubscriptionPrice(value[0])}
                            className="flex-1 max-w-xs"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          We recommend pricing between $4.99 - $9.99 based on your content frequency and quality.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className={`border rounded-lg p-4 ${
                monetizationModel === 'micropayment' ? 'border-primary bg-primary/5' : ''
              }`}>
                <div className="flex items-start gap-3">
                  <RadioGroupItem value="micropayment" id="micropayment" className="mt-1" />
                  <div>
                    <Label htmlFor="micropayment" className="font-medium">Per-Article Payments</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Readers pay a small fee for each article they want to read
                    </p>
                    
                    {monetizationModel === 'micropayment' && (
                      <div className="mt-4">
                        <Label htmlFor="article-price">Price Per Article ($)</Label>
                        <div className="flex items-center gap-3 mt-2">
                          <DollarSign className="h-4 w-4 text-muted-foreground" />
                          <Input
                            id="article-price"
                            type="number"
                            min="0.49"
                            max="9.99"
                            step="0.10"
                            value={articlePrice}
                            onChange={(e) => setArticlePrice(parseFloat(e.target.value))}
                            className="w-24"
                          />
                          <Slider
                            value={[articlePrice]}
                            min={0.49}
                            max={4.99}
                            step={0.10}
                            onValueChange={(value) => setArticlePrice(value[0])}
                            className="flex-1 max-w-xs"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          Most articles are priced between $0.99 - $1.99. Premium content can command higher prices.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className={`border rounded-lg p-4 ${
                monetizationModel === 'hybrid' ? 'border-primary bg-primary/5' : ''
              }`}>
                <div className="flex items-start gap-3">
                  <RadioGroupItem value="hybrid" id="hybrid" className="mt-1" />
                  <div>
                    <Label htmlFor="hybrid" className="font-medium">Hybrid Model</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Offer both subscription and per-article options to your readers
                    </p>
                    
                    {monetizationModel === 'hybrid' && (
                      <div className="mt-4 space-y-4">
                        <div>
                          <Label htmlFor="subscription-price-hybrid">Monthly Subscription Price ($)</Label>
                          <div className="flex items-center gap-3 mt-2">
                            <DollarSign className="h-4 w-4 text-muted-foreground" />
                            <Input
                              id="subscription-price-hybrid"
                              type="number"
                              min="0.99"
                              max="99.99"
                              step="1"
                              value={subscriptionPrice}
                              onChange={(e) => setSubscriptionPrice(parseFloat(e.target.value))}
                              className="w-24"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <Label htmlFor="article-price-hybrid">Price Per Article ($)</Label>
                          <div className="flex items-center gap-3 mt-2">
                            <DollarSign className="h-4 w-4 text-muted-foreground" />
                            <Input
                              id="article-price-hybrid"
                              type="number"
                              min="0.49"
                              max="9.99"
                              step="0.10"
                              value={articlePrice}
                              onChange={(e) => setArticlePrice(parseFloat(e.target.value))}
                              className="w-24"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </RadioGroup>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Revenue Sharing & Payments</CardTitle>
            <CardDescription>
              Understand how payments work on DirectPress
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-muted/30 p-4 rounded-lg">
              <h4 className="font-medium mb-2">Revenue Split</h4>
              <p className="text-sm text-muted-foreground mb-4">
                DirectPress operates on a 70/30 revenue split model:
              </p>
              <div className="flex flex-col space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm">You receive</span>
                  <span className="font-bold text-green-600">70%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full overflow-hidden">
                  <div className="bg-green-500 h-full rounded-full" style={{ width: '70%' }}></div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">DirectPress fee</span>
                  <span className="font-medium">30%</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                Our fee covers payment processing, platform maintenance, and reader acquisition.
              </p>
            </div>
            
            <div className="space-y-3">
              <h4 className="font-medium">Payment Schedule</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="border rounded-lg p-3">
                  <div className="flex items-center mb-2">
                    <Check className="h-4 w-4 text-green-500 mr-2" />
                    <span className="font-medium">Weekly Payouts</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Payments are processed every Monday for the previous week's earnings.
                  </p>
                </div>
                
                <div className="border rounded-lg p-3">
                  <div className="flex items-center mb-2">
                    <Check className="h-4 w-4 text-green-500 mr-2" />
                    <span className="font-medium">No Minimum</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    There's no minimum payout threshold. Earn $1 or $1,000, you'll get paid.
                  </p>
                </div>
                
                <div className="border rounded-lg p-3">
                  <div className="flex items-center mb-2">
                    <Check className="h-4 w-4 text-green-500 mr-2" />
                    <span className="font-medium">Automatic Deposits</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Earnings are automatically transferred to your connected bank account.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 pt-2">
              <Checkbox 
                id="terms" 
                checked={acceptedTerms}
                onCheckedChange={(checked) => {
                  setAcceptedTerms(checked as boolean);
                }}
              />
              <label
                htmlFor="terms"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                I'm ready to set up my payment account with Stripe Connect
              </label>
            </div>
            
            {acceptedTerms && (
              <div className="flex items-start space-x-4 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
                <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-blue-800 dark:text-blue-300">Next Steps</h4>
                  <p className="text-sm text-blue-700 dark:text-blue-400">
                    After clicking "Continue", you'll be redirected to Stripe to complete your account setup. 
                    You'll need your bank details and tax information.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </OnboardingLayout>
  );
};

export default MonetizationSetup;
